import { useState } from 'react';
import { Download, FileDown, Package } from 'lucide-react';
import { downloadFile, downloadFilesAsZip, downloadAllFiles } from '@/utils/download';
import type { FileType } from '@/routes/chat/hooks/use-chat';

interface DownloadButtonProps {
  files: FileType[];
  bootstrapFiles?: FileType[];
  currentFile?: FileType;
  projectName?: string;
  variant?: 'single' | 'all' | 'dropdown';
  className?: string;
  disabled?: boolean;
}

export function DownloadButton({
  files,
  bootstrapFiles = [],
  currentFile,
  projectName,
  variant = 'dropdown',
  className = '',
  disabled = false
}: DownloadButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleDownloadSingle = async () => {
    if (!currentFile) return;
    
    setIsDownloading(true);
    try {
      downloadFile(currentFile);
    } catch (error) {
      console.error('Failed to download file:', error);
    } finally {
      setIsDownloading(false);
      setShowDropdown(false);
    }
  };

  const handleDownloadAll = async () => {
    setIsDownloading(true);
    try {
      await downloadAllFiles(files, bootstrapFiles, projectName);
    } catch (error) {
      console.error('Failed to download all files:', error);
    } finally {
      setIsDownloading(false);
      setShowDropdown(false);
    }
  };

  const handleDownloadGenerated = async () => {
    setIsDownloading(true);
    try {
      await downloadFilesAsZip(files, `${projectName || 'project'}-generated`);
    } catch (error) {
      console.error('Failed to download generated files:', error);
    } finally {
      setIsDownloading(false);
      setShowDropdown(false);
    }
  };

  const hasFiles = files.length > 0 || bootstrapFiles.length > 0;

  if (variant === 'single' && currentFile) {
    return (
      <button
        onClick={handleDownloadSingle}
        disabled={disabled || isDownloading}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all duration-200 text-xs font-medium shadow-sm ${
          disabled || isDownloading
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        } ${className}`}
        title="Download current file"
      >
        <FileDown className="size-3" />
        {isDownloading ? 'Downloading...' : 'Download'}
      </button>
    );
  }

  if (variant === 'all') {
    return (
      <button
        onClick={handleDownloadAll}
        disabled={disabled || isDownloading || !hasFiles}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all duration-200 text-xs font-medium shadow-sm ${
          disabled || isDownloading || !hasFiles
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-green-600 hover:bg-green-700 text-white'
        } ${className}`}
        title="Download all files as ZIP"
      >
        <Package className="size-3" />
        {isDownloading ? 'Downloading...' : 'Download ZIP'}
      </button>
    );
  }

  // Dropdown variant
  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={disabled || isDownloading || !hasFiles}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all duration-200 text-xs font-medium shadow-sm ${
          disabled || isDownloading || !hasFiles
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        } ${className}`}
        title="Download options"
      >
        <Download className="size-3" />
        Download
      </button>

      {showDropdown && hasFiles && (
        <div className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 min-w-[180px]">
          {currentFile && (
            <button
              onClick={handleDownloadSingle}
              disabled={isDownloading}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <FileDown className="size-3" />
              Current File
            </button>
          )}
          
          {files.length > 0 && (
            <button
              onClick={handleDownloadGenerated}
              disabled={isDownloading}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <Package className="size-3" />
              Generated Files
            </button>
          )}
          
          <button
            onClick={handleDownloadAll}
            disabled={isDownloading}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 border-t border-gray-200 dark:border-gray-700"
          >
            <Download className="size-3" />
            All Files (ZIP)
          </button>
        </div>
      )}

      {/* Backdrop to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
}
